import { Lightning } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { ButtonConstant } from './ButtonConstants';
import { Base } from '../BaseComponent';
import { ButtonTSpec, buttonTypeConf } from './Button.conf';

interface ButtonModes {
  [buttonStyle: string]: any;
}

const Modes: ButtonModes = {
  primary: {
    normal: {
      border: true,
      borderColor: Theme.Colors.Button.Primary.Normal.Border,
      background: Theme.Colors.Button.Primary.Normal.Fill,
      foreground: Theme.Colors.Button.Primary.Normal.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.Primary.Focus.Border,
      background: Theme.Colors.Button.Primary.Focus.Fill,
      foreground: Theme.Colors.Button.Primary.Focus.Text,
    },
  },
  dialog: {
    normal: {
      background: Theme.Colors.Button.Dialog.Normal.background,
      foreground: Theme.Colors.Button.Dialog.Normal.foreground,
    },
    focus: {
      background: Theme.Colors.Button.Dialog.Focus.background,
      foreground: Theme.Colors.Button.Dialog.Focus.foreground,
    },
  },
  autoplayButton: {
    style: {
      radius: 20,
      horizontalPadding: 48,
    },
    normal: {
      background: Theme.Colors.Button.Dialog.Normal.background,
      foreground: Theme.Colors.Button.Dialog.Normal.foreground,
    },
    focus: {
      background: Theme.Colors.Button.Dialog.Focus.background,
      foreground: Theme.Colors.Button.Dialog.Focus.foreground,
    },
  },
  text: {
    normal: {
      background: Theme.Colors.Button.Primary.Normal.Fill,
      foreground: Theme.Colors.Button.Primary.Normal.Text,
    },
    focus: {
      background: Theme.Colors.Button.Primary.Focus.Fill,
      foreground: Theme.Colors.Button.Primary.Focus.Text,
    },
  },
  textButton: {
    normal: {
      background: Theme.Colors.Button.TextButton.Normal.Fill,
      foreground: Theme.Colors.Button.TextButton.Normal.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.TextButton.Focus.Border,
      background: Theme.Colors.Button.TextButton.Focus.Fill,
      foreground: Theme.Colors.Button.TextButton.Focus.Text,
    },
  },
  textButtonActivated: {
    normal: {
      background: Theme.Colors.Button.TextButton.Active.Fill,
      foreground: Theme.Colors.Button.TextButton.Active.Text,
    },
    focus: {
      border: true,
      borderColor: Theme.Colors.Button.TextButton.Focus.Border,
      background: Theme.Colors.Button.TextButton.Focus.Fill,
      foreground: Theme.Colors.Button.TextButton.Focus.Text,
    },
  },
};

export class Button extends Base<ButtonTSpec, buttonTypeConf> {
  private isFocused = false;
  private _buttonMode = 'primary';
  private _buttonActive = false;

  static override _template() {
    return {
      h: ButtonConstant.defaultH,
      flex: {
        direction: 'row' as const,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
        paddingLeft: ButtonConstant.horizontalPadding,
        paddingRight: ButtonConstant.horizontalPadding,
      },
      Background: {
        flexItem: false,
        w: (w: number) => w,
        h: (h: number) => h,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: ButtonConstant.cornerRadius,
        },
      },
      IconLeft: {
        texture: {
          type: Lightning.textures.ImageTexture,
        },
        visible: false,
      },
      IconLeftMargin: {
        flexItem: {
          marginRight: ButtonConstant.innerMargin,
        },
        visible: false,
      },
      Label: {
        text: {
          offsetY: 64,
          ...Theme.Fonts.Bold,
          wordWrap: false,
          verticalAlign: 'middle',
          textOverflow: 'ellipsis',
        },
      },
      IconRightMargin: {
        flexItem: {
          marginLeft: ButtonConstant.innerMargin,
        },
        visible: false,
      },
      IconRight: {
        texture: {
          type: Lightning.textures.ImageTexture,
        },
        visible: false,
      },
    };
  }

  override _init() {
    this.buttonActive = false;
    this.isFocused = false;
  }

  get buttonMode() {
    return this._buttonMode;
  }

  set buttonMode(buttonMode) {
    this._buttonMode = buttonMode;
    this._updateButtonLayout();
    this._updateButtonColors();
  }

  get buttonActive() {
    return this._buttonActive;
  }

  set buttonActive(active: boolean) {
    this._buttonActive = active;
    this._updateButtonColors();
  }

  get label() {
    const labelTag = this.getByRef('Label')!;
    const labelTexture = labelTag.text!;
    return labelTexture.text;
  }

  set label(label: string | undefined) {
    const elem = this.getByRef('Label')!;
    const elemTexture = elem.text!;
    elemTexture.text = label || '';

    if (label) {
      this.getByRef('IconLeftMargin')!.visible = !!this.iconLeft;
      this.getByRef('IconRightMargin')!.visible = !!this.iconRight;
    }
  }

  get iconLeft() {
    return this.getByRef('IconLeft')!.src;
  }

  set iconLeft(src: string | undefined) {
    this._setIcon(this.getByRef('IconLeft')!, src);
    let iconVisible = false;
    if (src != null && this.label) {
      iconVisible = true;
    }
    this.getByRef('IconLeftMargin')!.visible = iconVisible;
  }

  get iconRight() {
    return this.getByRef('IconRight')!.src;
  }

  set iconRight(src: string | undefined) {
    this._setIcon(this.getByRef('IconRight')!, src);
    let iconVisible = false;
    if (src != null && this.label) {
      iconVisible = true;
    }
    this.getByRef('IconRightMargin')!.visible = iconVisible;
  }

  _setIcon(icon: Lightning.Element, src: string | undefined) {
    if (src) {
      icon.src = src;
      icon.visible = true;
      icon.patch({
        texture: {
          src: src,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          resizeMode: {
            type: 'contain',
            h: ButtonConstant.iconSize,
          },
        },
      });
    } else {
      icon.visible = false;
    }
  }

  _getButtonModeString() {
    if (this.buttonActive) {
      return this.buttonMode + 'Activated';
    } else if (this.buttonMode in Modes) {
      return this.buttonMode;
    } else {
      // Reasonable default
      return 'primary';
    }
  }

  _updateButtonColors() {
    const mode = this._getButtonModeString();
    const state = this.isFocused ? 'focus' : 'normal';

    const currState = Modes[mode][state];
    this.getByRef('Background')!.shader!.stroke = currState.border ? ButtonConstant.stroke : 0;
    if (currState.border) this.getByRef('Background')!.shader!.strokeColor = currState.borderColor;
    this.getByRef('Background')!.shader!.fillColor = currState.background;

    for (const fgElem of ['IconLeft', 'Label', 'IconRight'] as const) {
      this.getByRef(fgElem)!.color = currState.foreground;
    }
  }

  _updateButtonLayout() {
    const mode = this._getButtonModeString();
    const currStyle = Modes[mode]['style'];
    if (currStyle) {
      this.patch({
        flex: {
          paddingLeft: currStyle.horizontalPadding,
          paddingRight: currStyle.horizontalPadding,
        },
        Background: {
          shader: {
            radius: currStyle.radius,
          },
        },
      });
    }
  }

  override _focus() {
    this.isFocused = true;
    this._updateButtonColors();
  }

  override _unfocus() {
    this.isFocused = false;
    this._updateButtonColors();
  }

  override _handleEnter() {
    this.signal('onActivate', this);
  }
}
